import React, { useState } from "react";
import { Assignment, ASSIGNMENT_MODE_INSURANCE } from "components/Assignment";
import { connect } from "react-redux";
import {
  getConfig,
  getZipCodes,
  getCaseLocations,
  getInspectorLocations,
  getCaseDetails,
  getAssignedCases,
  getInspectorDetails,
  removeCasesFromCaseDetailsList,
  removeCasesFromMap,
  clearMapSelection,
  clearInspectorDetails,
  assignCasesToInspector,
  unassignCases,
  clearAssignmentToolMemory,
  frGeoCoverageMode,
  saveFRServiceBoundary,
  getCasesWithAppts,
  updatePinsForAssignedCases,
} from "actions/assignment";
import { savePreference, loadPreference, loadPreferences } from "actions/preferences";
import { getCaseHeader, updateCaseHeader } from "actions/homeowner/caseforms";
import { getCountiesByState, getCountyBoundaries } from "actions/region";
import { getStateList } from "actions/appraiser";
import { updateHeader } from "actions/layout";
import { getFieldRepList } from "actions/homeowner/fieldReps";
import responseHandler from "responseHandler";
import { userHasRight } from "selectors/rights";
import { FIELD_REP_SERVICE_REGION_EDIT, FIELD_REP_SERVICE_REGION_VIEW, FIELD_REP_ASSIGNMENT_OVERRIDE } from "typeConstants/rights";
import useFRCoverage from "components/Assignment/useFRCoverage.tsx";
import AssignmentPinsProvider from "components/Assignment/AssignmentPinsProvider.tsx";

const mapStateToProps = (state) => ({
  ...state.assignment,
  isFetchingCasesWithAppointments: state.assignment.isFetchingCasesWithAppointments,
  assignedCasesWithAppts: state.assignment.assignedCasesWithAppts,
  selectedCaseHeader: state.caseForms.caseHeader,
  fetchingCaseHeader: state.caseForms.fetchingHeader,
  preferences: state.preferences,
  assignmentMode: ASSIGNMENT_MODE_INSURANCE,
  headerSearchText: state.search.searchText,
  lastSearchTime: state.search.lastSearchTime,
  stateList: state.appraiser.stateList,
  countyList: state.region.countiesByState,
  isFetchingCountyList: state.region.isFetchingCountiesByState,
  updatingCaseHeader: state.caseForms.updatingCaseHeader,
  FRList: state.fieldRep.fieldRepsDetailed,
  isFetchingFRList: state.fieldRep.isFetchingFieldReps,
  inspectorDetails: state.assignment.inspectorDetails,
  isFRGeoCoverageMode: state.assignment.isFRGeoCoverageMode,
  tempFRGeoServiceRegion: state.assignment.tempFRGeoServiceRegion,
	canEditFRServiceRegion: userHasRight(FIELD_REP_SERVICE_REGION_EDIT)(state),
  canViewFRServiceRegion: userHasRight(FIELD_REP_SERVICE_REGION_VIEW)(state),
  countyBoundaries: state.region.countyBoundaries,
  hasFrAssignmentOverride: userHasRight(FIELD_REP_ASSIGNMENT_OVERRIDE)(state),
});

const mapDispatchToProps = (dispatch) => ({
  updatePinsForAssignedCases(selectedInspectorNumber, selectedInspectorPinId, otherPinId) {
    dispatch(updatePinsForAssignedCases(selectedInspectorNumber, selectedInspectorPinId, otherPinId));
  },
  getCasesWithAppts(states, daysToLook) {
    dispatch(getCasesWithAppts(states, daysToLook));
  },
  getConfig() {
    dispatch(getConfig(ASSIGNMENT_MODE_INSURANCE));
  },
  getStateList() {
    dispatch(getStateList());
  },
  getZipCodes(stateCodeList) {
    dispatch(getZipCodes(stateCodeList));
  },
  getCountiesByState(stateCodeList) {
    dispatch(getCountiesByState(stateCodeList));
  },
  getCaseLocations(stateCodeList, caseIdFilter) {
    dispatch(getCaseLocations(ASSIGNMENT_MODE_INSURANCE, stateCodeList, caseIdFilter));
  },
  getInspectorLocations(stateCodeList, useBreadcrumbs) {
    dispatch(getInspectorLocations(ASSIGNMENT_MODE_INSURANCE, stateCodeList, useBreadcrumbs, true));
  },
  getCaseDetails(caseIds) {
    dispatch(getCaseDetails(ASSIGNMENT_MODE_INSURANCE, caseIds));
  },
  getAssignedCases(inspectorNumber) {
    dispatch(getAssignedCases(ASSIGNMENT_MODE_INSURANCE, inspectorNumber));
  },
  getInspectorDetails(inspectorNumbers) {
    dispatch(getInspectorDetails(ASSIGNMENT_MODE_INSURANCE, inspectorNumbers));
  },
  assignCasesToInspector(caseIds, inspectorId) {
    dispatch(assignCasesToInspector(ASSIGNMENT_MODE_INSURANCE, caseIds, inspectorId));
  },
  unassignCases(caseIds) {
    dispatch(unassignCases(ASSIGNMENT_MODE_INSURANCE, caseIds));
  },
  removeCasesFromCaseDetailsList(caseId) {
    dispatch(removeCasesFromCaseDetailsList(caseId));
  },
  removeCasesFromMap(caseIds) {
    dispatch(removeCasesFromMap(caseIds));
  },
  clearMapSelection() {
    dispatch(clearMapSelection());
  },
  clearInspectorDetails() {
    dispatch(clearInspectorDetails());
  },
  clearAssignmentToolMemory() {
    dispatch(clearAssignmentToolMemory());
  },
  getCaseHeader(caseId) {
    dispatch(getCaseHeader(caseId));
  },
  savePreference(code, value, callback) {
    dispatch(savePreference(code, value, callback));
  },
  loadPreference(code) {
    dispatch(loadPreference(code));
  },
  loadPreferences(codes) {
    dispatch(loadPreferences(codes));
  },
  updateHeader(headerDefinitions) {
    dispatch(updateHeader(headerDefinitions));
  },
  getFRList() {
    dispatch(getFieldRepList(""));
  },
  updateCaseHeader(controlNumber, dto) {
    dispatch(updateCaseHeader(controlNumber, dto));
  },
  frGeoCoverageMode(payload) {
    dispatch(frGeoCoverageMode(payload));
  },
  saveFRServiceBoundary(payload) {
    dispatch(saveFRServiceBoundary(payload));
  },
  makeToast(text, type) {
    responseHandler.customToast(text, type, dispatch);
  },
  getCountyBoundaries(countyIds) {
    dispatch(getCountyBoundaries(countyIds));
  }
});

const AssignmentWithHooks = props => {
  const [selectedStates, setSelectedStates] = useState([]);
  const [fieldReps, setFieldReps] = useState(null);
  const [showZipCoverage, setShowZipCoverage] = useState(false);
  const [showCoverage, setShowCoverage] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(new Date());
  const [showTooltips, setShowTooltips] = useState(true);

  const frCoverageHook = {
    data: useFRCoverage({ fieldReps, selectedStates, showZipCoverage, showCoverage, forceUpdate, showTooltips }),
    setSelectedStates,
    setFieldReps: frs => setFieldReps(frs.map(fr => ({ id: fr.id, name: fr.displayName, serviceRegions: fr.serviceRegions }))),
    setShowZipCoverage,
    setShowCoverage,
    setForceUpdate,
    showCoverage,
    showZipCoverage,
    setShowTooltips,
  };

  return (
    <AssignmentPinsProvider>
      <Assignment {...props} frCoverageHook={frCoverageHook} />
    </AssignmentPinsProvider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(AssignmentWithHooks);
